import {
  useModals,
  Box,
  Button,
  FontAwesomeIcon,
  LinkButtonTo,
  ListItem,
  Stack,
  Title,
} from "@skailer-com/uikit";
import { TimeOffCategoryPolicySchema } from "@/pages/settings/time-off/policy/TimeOffCategoryPolicyEdit.tsx";
import { TimeOffMilestoneEditModal } from "@/pages/settings/time-off/policy/widget/TimeOffMilestoneEditModal.tsx";
import {
  ApiTimeOffAccrualFrequencyTypes,
  ApiTimeOffPeriodTrackingTime,
  ApiTimeOffPolicy,
  ApiTimeOffPolicyMilestone,
  getAccrualFreqKeyMap,
} from "@/types/time-off/time-off-policy.types.ts";
import { sortMilestonesByStart } from "@/pages/settings/time-off/policy/time-off-policy.types.ts";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { useCallback, useMemo } from "react";
import { useFieldArray, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";

export interface TimeOffPolicyMilestonesProps {
  formHook: UseFormReturn<TimeOffCategoryPolicySchema>;
  timeOffPolicy?: ApiTimeOffPolicy;
  periodTrackType?: ApiTimeOffPeriodTrackingTime;
}

export function TimeOffPolicyMilestones({
  timeOffPolicy,
  formHook,
  periodTrackType,
}: TimeOffPolicyMilestonesProps) {
  const { t } = useTranslation();
  const { renderShortDate } = useFormatter();
  const { openModal } = useModals();
  const { control, getValues } = formHook;

  const isHours = periodTrackType === ApiTimeOffPeriodTrackingTime.hours;
  const accrualFreqKeyMap = useMemo(() => getAccrualFreqKeyMap(), []);

  const { replace: milestoneReplace } = useFieldArray({
    control,
    name: "milestones",
  });

  const replaceSorted = useCallback(
    (mutate: (milestones: ApiTimeOffPolicyMilestone[]) => ApiTimeOffPolicyMilestone[]) => {
      milestoneReplace(sortMilestonesByStart(mutate([...(getValues("milestones") ?? [])])));
    },
    [getValues, milestoneReplace]
  );

  const handleEditMilestone = useCallback(
    (milestone?: ApiTimeOffPolicyMilestone, index?: number) => {
      openModal(TimeOffMilestoneEditModal, {
        milestone,
        index,
        onModified: (modified: ApiTimeOffPolicyMilestone) => {
          replaceSorted((milestones) => {
            if (index !== undefined) {
              milestones[index] = modified;
            } else {
              milestones.push(modified);
            }
            return milestones;
          });
        },
        onDelete: () => {
          if (index === undefined) return;
          replaceSorted((milestones) => milestones.filter((_, i) => i !== index));
        },
        isHours,
      });
    },
    [timeOffPolicy, replaceSorted]
  );

  const getMilestoneCaption = (milestone: ApiTimeOffPolicyMilestone): string => {
    const rawFirst = milestone.accrual_frequency_data?.date_first ?? null;
    const rawSecond = milestone.accrual_frequency_data?.date_second ?? null;

    let datePart: string | null = null;
    switch (milestone.accrual_frequency_type) {
      case ApiTimeOffAccrualFrequencyTypes.monthly:
      case ApiTimeOffAccrualFrequencyTypes.monthly_annual_proportional: {
        const day = rawFirst ? new Date(rawFirst).getDate() : 1;
        datePart = t("time_off:milestone_list_caption_on_day", { day });
        break;
      }
      case ApiTimeOffAccrualFrequencyTypes.semimonthly: {
        const day1 = rawFirst ? new Date(rawFirst).getDate() : 1;
        const day2 = rawSecond ? new Date(rawSecond).getDate() : 1;
        datePart = t("time_off:milestone_list_caption_on_two_days", { day1, day2 });
        break;
      }
      case ApiTimeOffAccrualFrequencyTypes.yearly: {
        if (rawFirst)
          datePart = t("time_off:milestone_list_caption_on_date", {
            date: renderShortDate(new Date(rawFirst)),
          });
        break;
      }
      case ApiTimeOffAccrualFrequencyTypes.semiannual: {
        if (rawFirst && rawSecond)
          datePart = t("time_off:milestone_list_caption_on_two_dates", {
            date1: renderShortDate(new Date(rawFirst)),
            date2: renderShortDate(new Date(rawSecond)),
          });
        else if (rawFirst)
          datePart = t("time_off:milestone_list_caption_on_date", {
            date: renderShortDate(new Date(rawFirst)),
          });
        else if (rawSecond)
          datePart = t("time_off:milestone_list_caption_on_date", {
            date: renderShortDate(new Date(rawSecond)),
          });
        break;
      }
    }

    return [
      isHours
        ? t("plurals:plural_hours", { count: milestone.accrual_amount })
        : t("plurals:plural_day", { count: milestone.accrual_amount }),
      accrualFreqKeyMap[milestone.accrual_frequency_type],
      datePart,
    ]
      .filter(Boolean)
      .join(", ");
  };

  const extraMilestones = (getValues("milestones") ?? []).slice(1);

  return (
    <Stack>
      <Title
        header={t("time_off:milestones")}
        caption={t("time_off:milestones_description")}
        size={"xs"}
        paddingBottom
      />
      <Box variant={"border"}>
        <Stack className={"mb-4"}>
          {extraMilestones.length === 0 && t("common:empty")}
          {extraMilestones.map((milestone, sliceIndex) => {
            const index = sliceIndex + 1;
            return (
              <ListItem
                key={`policy_milestone_${index}`}
                title={t("time_off:milestone_list_title", {
                  amount: milestone.start_amount,
                  date_type: milestone.start_date_type,
                })}
                caption={getMilestoneCaption(milestone)}
                valueSlot={
                  <LinkButtonTo
                    onClick={() => {
                      handleEditMilestone(milestone, index);
                    }}
                  >
                    {t("common:edit")}
                  </LinkButtonTo>
                }
              />
            );
          })}
        </Stack>

        <Button
          onClick={() => {
            handleEditMilestone();
          }}
          variant={"secondary"}
          leftIcon={<FontAwesomeIcon icon={"fa-light fa-plus"} />}
        >
          {t("time_off:add_milestone")}
        </Button>
      </Box>
    </Stack>
  );
}
